import React, { useState, useEffect } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { supabase } from '../supabase/supabase';
import { useToast } from '../components/layout/ToastProvider';
import FormModal from '../components/common/FormModal';
import ConfirmModal from '../components/common/ConfirmModal';
import { motion, AnimatePresence, Reorder } from 'framer-motion';

const { 
  FiVideo, FiUsers, FiClock, FiPlus, FiCalendar, FiMoreVertical, 
  FiTrash2, FiExternalLink, FiDollarSign, FiUser, FiInfo, FiCopy,
  FiCheckCircle, FiAlertCircle, FiSettings, FiHelpCircle, FiX,
  FiGrabber, FiChevronUp, FiChevronDown, FiList, FiType, FiAlignLeft,
  FiActivity, FiZap, FiTarget, FiShield
} = FiIcons;

const PRESET_COLORS = ['#6366f1', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#0ea5e9'];

const QUESTION_TYPES = [
  { id: 'text', label: 'Short Answer', icon: FiType },
  { id: 'longtext', label: 'Long Text', icon: FiAlignLeft },
  { id: 'select', label: 'Multiple Choice', icon: FiList },
];

/**
 * Sub-component for individual Intake Question logic
 */
const IntakeQuestionItem = ({ q, idx, updateQuestion, removeQuestion }) => (
  <Reorder.Item 
    value={q}
    initial={{ opacity: 0, x: -20 }}
    animate={{ opacity: 1, x: 0 }}
    className="bg-white/[0.03] p-6 rounded-[2rem] border border-white/5 group/q hover:border-indigo-500/30 transition-all mb-4 backdrop-blur-sm"
  >
    <div className="flex flex-col md:flex-row gap-6">
      <div className="flex items-center gap-4">
        <div className="text-slate-600 cursor-grab active:cursor-grabbing hover:text-indigo-400 transition-colors">
          <SafeIcon icon={FiGrabber} />
        </div>
        <div className="w-10 h-10 rounded-xl bg-indigo-500/10 flex items-center justify-center text-indigo-400 font-black text-xs border border-indigo-500/20">
          {idx + 1}
        </div>
      </div>

      <div className="flex-1 space-y-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <input 
            value={q.text} 
            onChange={(e) => updateQuestion(q.id, 'text', e.target.value)}
            placeholder="Question text (e.g. What is your goal?)" 
            className="flex-1 bg-white/5 border border-white/10 rounded-2xl px-5 py-3 text-sm text-white outline-none focus:ring-2 focus:ring-indigo-500/50 transition-all" 
          />
          <select 
            value={q.type}
            onChange={(e) => updateQuestion(q.id, 'type', e.target.value)}
            className="bg-white/5 border border-white/10 rounded-2xl px-4 py-3 text-xs font-black text-slate-400 outline-none focus:ring-2 focus:ring-indigo-500/50 transition-all cursor-pointer"
          >
            {QUESTION_TYPES.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
          </select>
        </div>
        
        {q.type === 'select' && (
          <motion.div 
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            className="space-y-2 pl-4 border-l-2 border-indigo-500/20"
          >
            <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest block mb-1">Options (comma-separated)</label>
            <input 
              value={q.options?.join(', ')} 
              onChange={(e) => updateQuestion(q.id, 'options', e.target.value.split(',').map(s => s.trim()))}
              className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2 text-xs text-white outline-none focus:ring-1 focus:ring-indigo-500" 
              placeholder="e.g. Option 1, Option 2, Option 3"
            />
          </motion.div>
        )}
      </div>

      <div className="flex items-center gap-6">
        <label className="flex items-center gap-3 cursor-pointer group/check">
          <div className="relative flex items-center">
            <input 
              type="checkbox" 
              checked={q.required} 
              onChange={(e) => updateQuestion(q.id, 'required', e.target.checked)}
              className="peer w-5 h-5 rounded-lg border-white/10 bg-white/5 text-indigo-600 focus:ring-0 cursor-pointer transition-all appearance-none border checked:bg-indigo-600" 
            />
            <SafeIcon icon={FiCheckCircle} className="absolute inset-0 m-auto text-white opacity-0 peer-checked:opacity-100 pointer-events-none text-[10px]" />
          </div>
          <span className="text-[10px] font-black text-slate-500 uppercase tracking-[0.2em] group-hover/check:text-indigo-400 transition-colors">Required</span>
        </label>
        <button 
          type="button" 
          onClick={() => removeQuestion(q.id)} 
          className="w-10 h-10 rounded-xl bg-rose-500/10 text-rose-500/50 hover:text-rose-500 hover:bg-rose-500/20 transition-all flex items-center justify-center border border-rose-500/10"
        >
          <SafeIcon icon={FiTrash2} />
        </button>
      </div>
    </div>
  </Reorder.Item>
);

export default function Sessions() {
  const { addToast } = useToast();
  const [activeTab, setActiveTab] = useState('upcoming');
  const [sessionTypes, setSessionTypes] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const [isTypeModalOpen, setIsTypeModalOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isResponsesOpen, setIsResponsesOpen] = useState(false);
  const [selectedType, setSelectedType] = useState(null);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);
  const [intakeQuestions, setIntakeQuestions] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [typesRes, bookingsRes] = await Promise.all([
        supabase.from('session_types_2024').select('*').order('created_at', { ascending: false }),
        supabase.from('bookings_2024').select('*, client:clients_2024(*), type:session_types_2024(*)').order('start_time', { ascending: true })
      ]);
      setSessionTypes(typesRes.data || []);
      setBookings(bookingsRes.data || []);
    } catch (error) {
      addToast(error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const addQuestion = () => {
    setIntakeQuestions([...intakeQuestions, { 
      id: Math.random().toString(36).substr(2, 9), 
      text: '', 
      type: 'text', 
      required: false,
      options: [] 
    }]);
  };

  const removeQuestion = (id) => {
    setIntakeQuestions(intakeQuestions.filter(q => q.id !== id));
  };

  const updateQuestion = (id, field, value) => {
    setIntakeQuestions(intakeQuestions.map(q => q.id === id ? { ...q, [field]: value } : q));
  };

  const handleSaveType = async (e) => {
    e.preventDefault();
    setModalLoading(true);
    const formData = new FormData(e.target);
    
    const payload = {
      title: formData.get('title'),
      description: formData.get('description'),
      type: formData.get('type'),
      duration: parseInt(formData.get('duration')) || 60,
      price: parseFloat(formData.get('price')) || 0,
      max_participants: formData.get('type') === '1on1' ? 1 : (parseInt(formData.get('max_participants')) || 10),
      meeting_link: formData.get('meeting_link'),
      color_code: formData.get('color_code'),
      intake_questions: intakeQuestions,
      settings: {
        buffer_time: parseInt(formData.get('buffer_time')) || 15,
        reminders: formData.get('reminders') === 'on'
      }
    };

    try {
      if (selectedType) {
        const { error } = await supabase.from('session_types_2024').update(payload).eq('id', selectedType.id);
        if (error) throw error;
        addToast("Offering refined successfully!");
      } else {
        const { error } = await supabase.from('session_types_2024').insert([payload]);
        if (error) throw error;
        addToast("Premium offering created!");
      }
      setIsTypeModalOpen(false);
      fetchData();
    } catch (error) {
      addToast(error.message, 'error');
    } finally {
      setModalLoading(false);
    }
  };

  const handleDeleteType = async () => {
    try {
      const { error } = await supabase.from('session_types_2024').delete().eq('id', selectedType.id);
      if (error) throw error;
      addToast("Offering sunset successfully");
      setIsDeleteOpen(false);
      fetchData();
    } catch (error) {
      addToast(error.message, 'error');
    }
  };

  if (loading) return (
    <div className="h-[60vh] flex items-center justify-center">
      <div className="flex flex-col items-center gap-4">
        <div className="w-12 h-12 border-4 border-indigo-500/20 border-t-indigo-500 rounded-full animate-spin" />
        <p className="text-slate-400 font-mono text-[10px] uppercase tracking-[0.3em] animate-pulse">Syncing Session Intelligence...</p>
      </div>
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto space-y-12 pb-32 px-4">
      {/* Premium Header */}
      <header className="flex flex-col md:flex-row justify-between items-start md:items-end gap-8 bg-slate-900/40 backdrop-blur-3xl p-10 rounded-[3rem] border border-white/5 relative overflow-hidden group">
        <div className="absolute top-0 right-0 w-96 h-96 bg-indigo-500/5 blur-[120px] rounded-full -mr-48 -mt-48 transition-all group-hover:bg-indigo-500/10" />
        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-2xl bg-indigo-500/10 flex items-center justify-center text-indigo-400 border border-indigo-500/20">
              <SafeIcon icon={FiZap} />
            </div>
            <span className="text-[10px] font-black text-indigo-400 uppercase tracking-[0.4em]">Elite Architecture</span>
          </div>
          <h1 className="text-5xl font-black text-white tracking-tighter uppercase leading-none">Session Architect</h1>
          <p className="text-slate-400 mt-4 text-lg font-medium max-w-xl leading-relaxed">Systematize your high-performance coaching delivery with automated intake and premium session types.</p>
          
          <div className="flex gap-8 mt-10">
            {[
              { id: 'upcoming', label: 'Command Center', icon: FiActivity },
              { id: 'types', label: 'Premium Offerings', icon: FiTarget }
            ].map(tab => (
              <button 
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`pb-4 text-[11px] uppercase font-black tracking-[0.25em] transition-all relative flex items-center gap-3 ${activeTab === tab.id ? 'text-indigo-400' : 'text-slate-500 hover:text-slate-300'}`}
              >
                <SafeIcon icon={tab.icon} className="text-sm" />
                {tab.label}
                {activeTab === tab.id && <motion.div layoutId="tab-underline" className="absolute bottom-0 left-0 right-0 h-1 bg-indigo-500 rounded-full" />}
              </button>
            ))}
          </div>
        </div>
        
        <div className="flex gap-4 relative z-10">
          <button 
            onClick={() => { setSelectedType(null); setIntakeQuestions([]); setIsTypeModalOpen(true); }} 
            className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 text-white px-10 py-5 rounded-3xl font-black text-[11px] uppercase tracking-widest shadow-2xl shadow-indigo-600/30 transition-all flex items-center gap-3 transform hover:scale-105 active:scale-95"
          >
            <SafeIcon icon={FiPlus} className="text-lg" /> New Offering
          </button>
        </div>
      </header>

      <AnimatePresence mode="wait">
        {activeTab === 'upcoming' ? (
          <motion.div 
            initial={{ opacity: 0, y: 20 }} 
            animate={{ opacity: 1, y: 0 }} 
            exit={{ opacity: 0, y: -20 }} 
            key="upcoming" 
            className="space-y-6"
          >
            {bookings.length === 0 ? (
              <div className="bg-white/[0.02] border border-dashed border-white/10 rounded-[3rem] p-32 text-center group hover:border-indigo-500/30 transition-all">
                <div className="w-24 h-24 bg-indigo-500/5 rounded-[2rem] flex items-center justify-center mx-auto mb-8 border border-white/5 group-hover:scale-110 transition-transform">
                  <SafeIcon icon={FiCalendar} className="text-4xl text-slate-600 group-hover:text-indigo-400 transition-colors" />
                </div>
                <h3 className="text-2xl font-black text-white uppercase tracking-tight mb-4">Command Center Silent</h3>
                <p className="text-slate-500 max-w-sm mx-auto font-medium">No upcoming sessions detected in your current orbit. Start by defining your first premium offering.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-6">
                {bookings.map((booking) => (
                  <motion.div 
                    layout
                    key={booking.id} 
                    className="bg-white/[0.02] border border-white/5 rounded-[3rem] p-10 flex flex-col lg:flex-row items-center gap-10 hover:bg-white/[0.05] transition-all backdrop-blur-2xl group relative overflow-hidden"
                  >
                    <div className="absolute top-0 left-0 w-1 h-full bg-indigo-500 opacity-0 group-hover:opacity-100 transition-opacity" />
                    
                    <div className="flex items-center gap-8 w-full lg:w-auto">
                      <div className="w-28 h-28 bg-white/5 rounded-[2.5rem] flex flex-col items-center justify-center border border-white/10 relative overflow-hidden group-hover:border-indigo-500/30 transition-colors">
                        <div className="absolute top-0 inset-x-0 h-1" style={{ backgroundColor: booking.type?.color_code || '#6366f1' }} />
                        <span className="text-[11px] uppercase font-black text-slate-500 mb-1">{new Date(booking.start_time).toLocaleDateString('en-US', { month: 'short' })}</span>
                        <span className="text-4xl font-black text-white tracking-tighter">{new Date(booking.start_time).getDate()}</span>
                      </div>
                      
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <span className="px-3 py-1 rounded-full bg-indigo-500/10 text-indigo-400 text-[8px] font-black uppercase tracking-widest border border-indigo-500/20">
                            {booking.type?.type === '1on1' ? 'Solo Session' : 'Group Mastermind'}
                          </span>
                          <span className="text-[10px] text-slate-500 font-bold uppercase tracking-widest flex items-center gap-2">
                            <SafeIcon icon={FiClock} /> {new Date(booking.start_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </span>
                        </div>
                        <h3 className="font-black text-white text-3xl tracking-tighter group-hover:text-indigo-400 transition-colors">{booking.type?.title}</h3>
                        <div className="flex items-center gap-4 mt-4">
                          <div className="flex items-center gap-3 bg-white/5 px-5 py-2.5 rounded-2xl border border-white/5">
                            <div className="w-6 h-6 rounded-lg bg-indigo-600 flex items-center justify-center text-[10px] font-black text-white">
                              {booking.client?.name?.charAt(0)}
                            </div>
                            <span className="text-xs font-bold text-slate-300">{booking.client?.name}</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="flex-1 w-full lg:w-auto border-t lg:border-t-0 lg:border-l border-white/5 pt-8 lg:pt-0 lg:pl-10">
                      <div className="flex items-start gap-4">
                        <div className="w-10 h-10 rounded-xl bg-white/5 flex items-center justify-center text-slate-600 mt-1">
                          <SafeIcon icon={FiAlignLeft} />
                        </div>
                        <div>
                          <label className="text-[9px] font-black text-slate-600 uppercase tracking-widest block mb-2">Session Directives</label>
                          <p className="text-slate-400 text-sm leading-relaxed font-medium italic">
                            "{booking.notes || "High-intensity performance session focusing on strategic architecture and system scaling."}"
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="flex gap-4 w-full lg:w-auto mt-6 lg:mt-0">
                      {booking.type?.intake_questions?.length > 0 && (
                        <button 
                          onClick={() => { setSelectedBooking(booking); setIsResponsesOpen(true); }} 
                          className="flex-1 lg:flex-none bg-white/5 hover:bg-white/10 text-white px-8 py-5 rounded-[1.5rem] text-[10px] font-black uppercase tracking-widest border border-white/10 transition-all flex items-center justify-center gap-3"
                        >
                          <SafeIcon icon={FiList} className="text-lg" /> Intel
                        </button>
                      )}
                      <a 
                        href={booking.type?.meeting_link} 
                        target="_blank" 
                        rel="noreferrer" 
                        className="flex-1 lg:flex-none bg-indigo-600 hover:bg-indigo-700 text-white px-10 py-5 rounded-[1.5rem] text-[10px] font-black uppercase tracking-widest shadow-xl shadow-indigo-600/20 transition-all flex items-center justify-center gap-3"
                      >
                        <SafeIcon icon={FiVideo} className="text-lg" /> Join Live
                      </a>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </motion.div>
        ) : (
          <motion.div 
            initial={{ opacity: 0, y: 20 }} 
            animate={{ opacity: 1, y: 0 }} 
            exit={{ opacity: 0, y: -20 }} 
            key="types" 
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
          >
            {sessionTypes.map((type) => (
              <div 
                key={type.id} 
                className="bg-white/[0.02] border border-white/5 rounded-[4rem] p-12 flex flex-col group relative overflow-hidden backdrop-blur-3xl hover:bg-white/[0.04] transition-all"
              >
                <div className="absolute top-0 right-0 w-80 h-80 blur-[140px] opacity-10 -mr-32 -mt-32 transition-transform group-hover:scale-110" style={{ backgroundColor: type.color_code }} />
                
                <div className="flex justify-between items-start mb-12 relative z-10">
                  <div className="flex gap-2">
                    <button 
                      onClick={() => { setSelectedType(type); setIntakeQuestions(type.intake_questions || []); setIsTypeModalOpen(true); }} 
                      className="w-14 h-14 bg-white/5 text-slate-400 rounded-3xl flex items-center justify-center hover:text-white hover:bg-indigo-600 transition-all border border-white/10 shadow-xl"
                    >
                      <SafeIcon icon={FiSettings} className="text-xl" />
                    </button>
                  </div>
                  <div className="text-right">
                    <span className="text-4xl font-black text-white tracking-tighter">${type.price}</span>
                    <p className="text-[9px] font-black text-slate-500 uppercase tracking-widest mt-1">Per Session</p>
                  </div>
                </div>

                <div className="mb-10 relative z-10">
                  <h3 className="text-3xl font-black text-white mb-4 tracking-tighter group-hover:text-indigo-400 transition-colors">{type.title}</h3>
                  <p className="text-sm text-slate-400 leading-relaxed font-medium line-clamp-3 mb-8">{type.description || "No description provided for this premium offering."}</p>
                  
                  <div className="flex flex-wrap gap-3">
                    <div className="flex items-center gap-2 bg-white/5 px-4 py-2 rounded-2xl border border-white/5 text-[9px] font-black text-slate-400 uppercase tracking-widest">
                      <SafeIcon icon={FiClock} className="text-indigo-400" /> {type.duration}m
                    </div>
                    <div className="flex items-center gap-2 bg-white/5 px-4 py-2 rounded-2xl border border-white/5 text-[9px] font-black text-slate-400 uppercase tracking-widest">
                      <SafeIcon icon={type.type === '1on1' ? FiUser : FiUsers} className="text-indigo-400" /> {type.type === '1on1' ? 'Solo' : `${type.max_participants} Max`}
                    </div>
                    {type.intake_questions?.length > 0 && (
                      <div className="flex items-center gap-2 bg-indigo-500/10 px-4 py-2 rounded-2xl border border-indigo-500/20 text-[9px] font-black text-indigo-400 uppercase tracking-widest">
                        <SafeIcon icon={FiShield} /> Intake Active
                      </div>
                    )}
                  </div>
                </div>

                <div className="mt-auto flex justify-between items-center border-t border-white/5 pt-10 relative z-10">
                  <div className="flex items-center gap-3">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: type.color_code }} />
                    <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Global Offering</span>
                  </div>
                  <button 
                    onClick={() => { setSelectedType(type); setIsDeleteOpen(true); }} 
                    className="w-10 h-10 rounded-xl text-rose-500/30 hover:text-rose-500 hover:bg-rose-500/10 transition-all flex items-center justify-center"
                  >
                    <SafeIcon icon={FiTrash2} />
                  </button>
                </div>
              </div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Offering Creation Modal */}
      <FormModal 
        isOpen={isTypeModalOpen} 
        onClose={() => setIsTypeModalOpen(false)} 
        title={selectedType ? "Refine Session Offering" : "Design New High-Performance Offering"} 
        onSubmit={handleSaveType} 
        loading={modalLoading} 
        width="max-w-4xl"
      >
        <div className="space-y-12 max-h-[75vh] overflow-y-auto pr-6 custom-scrollbar pb-10">
          {/* Identity Section */}
          <section className="space-y-8">
            <div className="flex items-center gap-4 border-b border-white/5 pb-4">
              <div className="w-10 h-10 rounded-2xl bg-indigo-500/10 flex items-center justify-center text-indigo-400">
                <SafeIcon icon={FiTarget} />
              </div>
              <h3 className="text-lg font-black text-white uppercase tracking-tight">Core Identity</h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="space-y-3">
                <label className="text-[10px] font-black text-slate-500 uppercase tracking-[0.3em]">Offering Title</label>
                <input 
                  name="title" 
                  defaultValue={selectedType?.title} 
                  required 
                  className="w-full bg-white/5 border border-white/10 rounded-3xl px-8 py-5 text-sm text-white font-bold outline-none focus:ring-2 focus:ring-indigo-500/50 transition-all" 
                  placeholder="e.g. Scaling Mastermind Intensive" 
                />
              </div>
              <div className="space-y-3">
                <label className="text-[10px] font-black text-slate-500 uppercase tracking-[0.3em]">Visual Theme</label>
                <div className="flex flex-wrap gap-3">
                  {PRESET_COLORS.map(c => (
                    <label key={c} className="cursor-pointer">
                      <input type="radio" name="color_code" value={c} defaultChecked={selectedType?.color_code === c || (c === '#6366f1' && !selectedType)} className="sr-only peer" />
                      <div className="w-12 h-12 rounded-[1.25rem] border-2 border-transparent peer-checked:border-white peer-checked:scale-110 transition-all shadow-xl" style={{ backgroundColor: c }} />
                    </label>
                  ))}
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <label className="text-[10px] font-black text-slate-500 uppercase tracking-[0.3em]">Session Directives (Description)</label>
              <textarea 
                name="description" 
                defaultValue={selectedType?.description} 
                className="w-full bg-white/5 border border-white/10 rounded-[2rem] px-8 py-6 text-sm text-white font-medium outline-none focus:ring-2 focus:ring-indigo-500/50 transition-all h-32 resize-none" 
                placeholder="Clarify the objectives and expected outcomes..." 
              />
            </div>
          </section>

          {/* Logistics Section */}
          <section className="space-y-8">
            <div className="flex items-center gap-4 border-b border-white/5 pb-4">
              <div className="w-10 h-10 rounded-2xl bg-indigo-500/10 flex items-center justify-center text-indigo-400">
                <SafeIcon icon={FiActivity} />
              </div>
              <h3 className="text-lg font-black text-white uppercase tracking-tight">Delivery Logistics</h3>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="space-y-3">
                <label className="text-[10px] font-black text-slate-500 uppercase tracking-[0.3em]">Format</label>
                <select 
                  name="type" 
                  defaultValue={selectedType?.type || '1on1'}
                  className="w-full bg-white/5 border border-white/10 rounded-2xl px-6 py-4 text-xs font-black text-white outline-none focus:ring-2 focus:ring-indigo-500/50 appearance-none"
                >
                  <option value="1on1">1-ON-1 SOLO</option>
                  <option value="group">GROUP MASTERMIND</option>
                </select>
              </div>
              <div className="space-y-3">
                <label className="text-[10px] font-black text-slate-500 uppercase tracking-[0.3em]">Duration (Min)</label>
                <input 
                  name="duration" 
                  type="number"
                  defaultValue={selectedType?.duration || 60} 
                  className="w-full bg-white/5 border border-white/10 rounded-2xl px-6 py-4 text-xs font-black text-white outline-none focus:ring-2 focus:ring-indigo-500/50" 
                />
              </div>
              <div className="space-y-3">
                <label className="text-[10px] font-black text-slate-500 uppercase tracking-[0.3em]">Price ($)</label>
                <input 
                  name="price" 
                  type="number"
                  defaultValue={selectedType?.price || 0} 
                  className="w-full bg-white/5 border border-white/10 rounded-2xl px-6 py-4 text-xs font-black text-white outline-none focus:ring-2 focus:ring-indigo-500/50" 
                />
              </div>
              <div className="space-y-3">
                <label className="text-[10px] font-black text-slate-500 uppercase tracking-[0.3em]">Buffer Time</label>
                <input 
                  name="buffer_time" 
                  type="number"
                  defaultValue={selectedType?.settings?.buffer_time || 15} 
                  className="w-full bg-white/5 border border-white/10 rounded-2xl px-6 py-4 text-xs font-black text-white outline-none focus:ring-2 focus:ring-indigo-500/50" 
                />
              </div>
            </div>

            <div className="space-y-3">
              <label className="text-[10px] font-black text-slate-500 uppercase tracking-[0.3em]">Permanent Meeting Link (Zoom/Meet)</label>
              <div className="relative">
                <SafeIcon icon={FiVideo} className="absolute left-6 top-1/2 -translate-y-1/2 text-slate-500" />
                <input 
                  name="meeting_link" 
                  defaultValue={selectedType?.meeting_link} 
                  className="w-full bg-white/5 border border-white/10 rounded-3xl pl-16 pr-8 py-5 text-sm text-white font-bold outline-none focus:ring-2 focus:ring-indigo-500/50 transition-all" 
                  placeholder="https://zoom.us/j/..." 
                />
              </div>
            </div>
          </section>

          {/* Intake Management */}
          <section className="space-y-8">
            <div className="flex justify-between items-center border-b border-white/5 pb-4">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-2xl bg-indigo-500/10 flex items-center justify-center text-indigo-400">
                  <SafeIcon icon={FiShield} />
                </div>
                <h3 className="text-lg font-black text-white uppercase tracking-tight">Intake Intelligence</h3>
              </div>
              <button 
                type="button" 
                onClick={addQuestion} 
                className="bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-3 rounded-2xl text-[10px] font-black uppercase tracking-widest transition-all shadow-lg shadow-indigo-600/20 flex items-center gap-2"
              >
                <SafeIcon icon={FiPlus} /> Add Intelligence Node
              </button>
            </div>
            
            <Reorder.Group axis="y" values={intakeQuestions} onReorder={setIntakeQuestions} className="space-y-4">
              {intakeQuestions.map((q, idx) => (
                <IntakeQuestionItem 
                  key={q.id} q={q} idx={idx} 
                  updateQuestion={updateQuestion} 
                  removeQuestion={removeQuestion} 
                />
              ))}
              {intakeQuestions.length === 0 && (
                <div className="py-12 bg-white/[0.02] border border-dashed border-white/10 rounded-[2rem] text-center">
                  <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest">No custom intake questions defined.</p>
                </div>
              )}
            </Reorder.Group>
          </section>
        </div>
      </FormModal>

      {/* Intake Intelligence Viewer */}
      <FormModal
        isOpen={isResponsesOpen} 
        onClose={() => setIsResponsesOpen(false)}
        title="Session Intake Intelligence" 
        width="max-w-2xl" 
        showFooter={false}
      >
        <div className="space-y-10 py-4">
           <div className="flex items-center gap-6 p-8 bg-white/5 rounded-[2.5rem] border border-white/10 relative overflow-hidden group">
              <div className="absolute top-0 right-0 w-32 h-32 bg-indigo-500/10 blur-3xl" />
              <div className="w-20 h-20 bg-indigo-600/20 rounded-[1.5rem] flex items-center justify-center text-indigo-400 text-3xl border border-indigo-500/20 relative z-10">
                <SafeIcon icon={FiUser} />
              </div>
              <div className="relative z-10">
                <h3 className="text-3xl font-black text-white tracking-tighter uppercase">{selectedBooking?.client?.name}</h3>
                <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest mt-1">Strategic Partner Intel</p>
              </div>
           </div>

           <div className="space-y-8">
              {selectedBooking?.type?.intake_questions?.map((q, idx) => (
                <motion.div 
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: idx * 0.1 }}
                  key={q.id} 
                  className="space-y-4"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center text-slate-500 text-[10px] font-black border border-white/5">
                      {idx + 1}
                    </div>
                    <label className="text-xs font-black text-white uppercase tracking-wider">{q.text}</label>
                  </div>
                  <div className="p-8 bg-white/[0.03] rounded-[2rem] border border-white/10 text-slate-300 font-medium leading-relaxed shadow-inner">
                    {selectedBooking.intake_responses?.[q.id] || <span className="text-slate-600 italic">No response provided by client.</span>}
                  </div>
                </motion.div>
              ))}
           </div>

           <div className="pt-8 border-t border-white/5 flex justify-end">
              <button 
                onClick={() => setIsResponsesOpen(false)}
                className="bg-indigo-600 hover:bg-indigo-700 text-white px-10 py-4 rounded-2xl text-[10px] font-black uppercase tracking-widest transition-all"
              >
                Acknowledge Intel
              </button>
           </div>
        </div>
      </FormModal>

      <ConfirmModal 
        isOpen={isDeleteOpen} 
        onClose={() => setIsDeleteOpen(false)} 
        onConfirm={handleDeleteType}
        title="Sunset Premium Offering?" 
        message="Permanently remove this session type from your architecture? Active bookings will remain but no new ones can be scheduled."
      />

      {/* Strategic Floating Status */}
      <motion.div 
        initial={{ y: 100 }}
        animate={{ y: 0 }}
        className="fixed bottom-10 left-1/2 -translate-x-1/2 z-50 px-10 py-5 bg-slate-900/80 backdrop-blur-3xl border border-white/10 rounded-full shadow-2xl flex items-center gap-8"
      >
        <div className="flex items-center gap-3">
          <div className="w-2 h-2 bg-indigo-500 rounded-full animate-pulse" />
          <span className="text-[10px] font-black text-white uppercase tracking-widest">Architect Online</span>
        </div>
        <div className="h-5 w-px bg-white/10" />
        <div className="flex items-center gap-3">
          <SafeIcon icon={FiShield} className="text-indigo-400" />
          <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Intake Intelligence Active</span>
        </div>
      </motion.div>
    </div>
  );
}